import {
  convertToKST,
  getPrimaryColor,
  isKST,
  JEJURO,
  Modal,
  useGetCompanyInfo,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { Dropdown } from '@/components/dropdown';
import {
  getAvailablePickUpTimeOptions,
  getRentalOperatingHours,
  getTimeOptions,
} from '@/utils/date';
import { DateSelection } from '../dateSelection/DateSelection';

type HeroDateModalProps = {
  pickUpDate: Dayjs;
  setPickUpDate: Dispatch<SetStateAction<Dayjs>>;
  dropOffDate: Dayjs;
  setDropOffDate: Dispatch<SetStateAction<Dayjs>>;
  pickUpTimeValue: number;
  dropOffTimeValue: number;
  setPickUpTimeValue: Dispatch<SetStateAction<number>>;
  setDropOffTimeValue: Dispatch<SetStateAction<number>>;
  onClose: () => void;
};

const HeroDateModal = ({
  pickUpDate,
  setPickUpDate,
  dropOffDate,
  setDropOffDate,
  pickUpTimeValue,
  dropOffTimeValue,
  setPickUpTimeValue,
  setDropOffTimeValue,
  onClose,
}: HeroDateModalProps) => {
  const { data: companyData } = useGetCompanyInfo();
  const rentalOperatingHours = getRentalOperatingHours(companyData);

  const todayInKST = dayjs().tz('Asia/seoul');
  const pickUpAtInKST = dayjs(convertToKST(pickUpDate?.format())).tz(
    'Asia/seoul',
  );
  const isToday = pickUpAtInKST.isSame(todayInKST, 'date');

  const pickUpTimeOptions = getTimeOptions('pickUp', rentalOperatingHours);
  const dropOffTimeOptions = getTimeOptions('dropOff', rentalOperatingHours);
  const availablePickUpTimeOptions =
    getAvailablePickUpTimeOptions(rentalOperatingHours);

  const pickUpOptions = isToday
    ? availablePickUpTimeOptions
    : pickUpTimeOptions;
  const dropOffOptions = pickUpDate?.isSame(dropOffDate, 'date')
    ? dropOffTimeOptions.filter((option) => option.value > pickUpTimeValue)
    : dropOffTimeOptions;

  const isEarlyDropOff = dropOffTimeValue <= 730;

  return (
    <Modal
      onClose={onClose}
      paddingVertical={28}
      paddingHorizontal={28}
      hasBorder={false}
      topLevel
    >
      <Content>
        <ModalTitle>이용일을 선택하세요</ModalTitle>

        <DateSelection
          pickUpDate={pickUpDate}
          setPickUpDate={setPickUpDate}
          dropOffDate={dropOffDate}
          setDropOffDate={setDropOffDate}
        />

        <TimeRow>
          <TimeField>
            <TimeLabel>대여 시간</TimeLabel>
            <Dropdown
              width='100%'
              options={pickUpOptions}
              selectedValue={pickUpTimeValue}
              onSelect={setPickUpTimeValue}
            />
          </TimeField>
          <TimeField>
            <TimeLabel>반납 시간</TimeLabel>
            <Dropdown
              width='100%'
              options={dropOffOptions}
              selectedValue={dropOffTimeValue}
              onSelect={setDropOffTimeValue}
            />
          </TimeField>
        </TimeRow>

        {isEarlyDropOff && (
          <Warning>
            선택하신 반납 시간은 새벽 반납 비용 1만원이 추가됩니다.
          </Warning>
        )}
        {!isKST && <Notice>한국 시간 기준입니다.</Notice>}

        <Footer>
          <ApplyButton onClick={onClose}>적용</ApplyButton>
        </Footer>
      </Content>
    </Modal>
  );
};

export default HeroDateModal;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 320px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  ${({ theme }) => theme.FONT.H4};
  letter-spacing: -0.02em;
`;

const TimeRow = styled.div`
  display: flex;
  gap: 16px;
`;

/* Dropdown이 position:absolute라 relative + 높이 확보 컨테이너가 필요하다. */
const TimeField = styled.div`
  flex: 1;
  position: relative;
  height: 78px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TimeLabel = styled.span`
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const Warning = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.DANGER};
`;

const Notice = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ApplyButton = styled.button`
  padding: 12px 32px;
  border: none;
  border-radius: 10px;
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.92;
  }
`;
